import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

async function callClaude(prompt, maxTokens = 1000) {
  const res = await fetch(`${API_URL}/api/content/meta-batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, maxTokens })
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.result;
}

function CharCounter({ text, limit }) {
  const len = (text || '').length;
  const color = len > limit ? 'var(--red)' : len > limit * 0.9 ? 'var(--amber)' : 'var(--green)';
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color }}>
      {len}/{limit} {len > limit ? '⚠ Over limit' : '✓'}
    </span>
  );
}

function MetaResult({ label, value, limit, onRegenerate, regenerating, onCopy }) {
  const [edited, setEdited] = useState(value);

  React.useEffect(() => setEdited(value), [value]);

  return (
    <div style={{
      background: 'var(--gray-50)',
      border: `1px solid ${(edited || '').length > limit ? '#F09595' : 'var(--gray-200)'}`,
      borderRadius: 'var(--border-radius-md, 8px)',
      padding: '12px 14px',
      marginBottom: 10
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CharCounter text={edited} limit={limit} />
          <button
            className="btn btn-sm"
            onClick={onRegenerate}
            disabled={regenerating}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            {regenerating ? <><span className="spinner" /> …</> : '↻ New'}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => { navigator.clipboard.writeText(edited); onCopy(); }}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            Copy
          </button>
        </div>
      </div>
      <textarea
        value={edited}
        onChange={e => setEdited(e.target.value)}
        rows={label.toLowerCase().includes('title') ? 2 : 3}
        style={{
          width: '100%', border: 'none', background: 'transparent',
          fontSize: 13, fontWeight: 500, resize: 'none', outline: 'none',
          color: 'var(--gray-900)', lineHeight: 1.5, fontFamily: 'inherit'
        }}
      />
    </div>
  );
}

export default function MetaGenerator({ onBack }) {
  const [form, setForm] = useState({
    keyword: '',
    location: '',
    businessName: '',
    pageType: 'service',
    usp: '',
    phone: '',
    clientId: '',
  });
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [results, setResults] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [regenIndex, setRegenIndex] = useState(null);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  React.useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    const { data } = await supabase.from('clients').select('id, name, url').order('name');
    if (data) setClients(data);
    setLoadingClients(false);
  };

  const update = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleCopy = (label) => {
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const buildPrompt = (variationHint = '') => {
    const keyword = form.keyword.trim();
    const location = form.location.trim();
    const business = form.businessName.trim();
    const fullKeyword = location ? `${keyword} ${location}` : keyword;

    return `You are an SEO expert at a UK digital marketing agency. Generate optimised meta data following these strict rules:

TARGET KEYWORD: "${fullKeyword}"
BUSINESS NAME: "${business}"
PAGE TYPE: ${form.pageType}
USP / OFFER: ${form.usp || 'Not provided'}
PHONE: ${form.phone || 'Not provided'}
${variationHint ? `VARIATION HINT: ${variationHint}` : ''}

TITLE TAG RULES (CRITICAL):
- MUST be 60 characters or fewer — count every single character
- Start with the target keyword or very close variation
- Include business name at the end after " | "
- Be compelling and click-worthy
- Use UK English
- Can include: price ("From £X"), year ("Since 2008"), phone, local area, persuasive hook
- Examples: "Carpet Cleaning Bromley | Since 2008 | Carpet Bright UK", "Window Cleaner Edenbridge - From £15 | Pure Window Cleaning"

META DESCRIPTION RULES (CRITICAL):
- MUST be 156 characters or fewer — count every single character
- Include the target keyword naturally in the first sentence
- Include a clear call to action
- Mention key benefits or USP if provided
- Use UK English
- Be compelling enough to earn the click

Respond ONLY with valid JSON, no markdown:
{
  "titleTags": [
    "title tag variation 1",
    "title tag variation 2", 
    "title tag variation 3"
  ],
  "metaDescriptions": [
    "meta description variation 1",
    "meta description variation 2",
    "meta description variation 3"
  ]
}

Generate 3 distinct variations for each. Each must be genuinely different in approach (e.g. one uses price, one uses phone, one uses a benefit/hook). Count characters carefully — no exceptions on limits.`;
  };

  const generate = async () => {
    if (!form.keyword.trim() || !form.businessName.trim()) {
      setError('Please enter at least a keyword and business name.');
      return;
    }
    setError('');
    setGenerating(true);
    setResults(null);
    try {
      const text = await callClaude(buildPrompt());
      const clean = text.replace(/```json|```/g, '').trim();
      setResults(JSON.parse(clean));
    } catch (e) {
      setError('Failed to generate — please try again.');
    }
    setGenerating(false);
  };

  const regenerateOne = async (type, index) => {
    const key = `${type}-${index}`;
    setRegenIndex(key);
    try {
      const hints = ['try a different angle or hook', 'focus on urgency or social proof', 'lead with a question or benefit'];
      const text = await callClaude(buildPrompt(hints[index % hints.length]));
      const clean = text.replace(/```json|```/g, '').trim();
      const fresh = JSON.parse(clean);
      setResults(prev => {
        const updated = { ...prev };
        if (type === 'title') updated.titleTags[index] = fresh.titleTags[index];
        else updated.metaDescriptions[index] = fresh.metaDescriptions[index];
        return updated;
      });
    } catch (e) {
      setError('Failed to regenerate — please try again.');
    }
    setRegenIndex(null);
  };

  const copyAll = () => {
    if (!results) return;
    const text = [
      'TITLE TAGS',
      ...results.titleTags.map((t, i) => `${i + 1}. ${t}`),
      '',
      'META DESCRIPTIONS',
      ...results.metaDescriptions.map((d, i) => `${i + 1}. ${d}`)
    ].join('\n');
    navigator.clipboard.writeText(text);
    handleCopy('all');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Meta Data Generator</div>
          <div className="page-sub">Generate optimised title tags and meta descriptions following IMSR best practices</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

        {/* Left — input form */}
        <div>
          <div className="card">
            <div className="card-title">Page details</div>
            <div className="card-sub">Fill in what you know — more detail means better output.</div>

            {/* Optional client link */}
            <div className="form-group">
              <label>Link to client (optional)</label>
              <select
                value={form.clientId}
                onChange={e => {
                  const c = clients.find(c => c.id === e.target.value);
                  update('clientId', e.target.value);
                  if (c) update('businessName', c.name);
                }}
              >
                <option value="">— No client selected —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-row form-row-2 form-group">
              <div>
                <label>Target keyword *</label>
                <input
                  type="text"
                  placeholder="e.g. carpet cleaning"
                  value={form.keyword}
                  onChange={e => update('keyword', e.target.value)}
                />
              </div>
              <div>
                <label>Location (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Bromley"
                  value={form.location}
                  onChange={e => update('location', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row form-row-2 form-group">
              <div>
                <label>Business name *</label>
                <input
                  type="text"
                  placeholder="e.g. Carpet Bright UK"
                  value={form.businessName}
                  onChange={e => update('businessName', e.target.value)}
                />
              </div>
              <div>
                <label>Page type</label>
                <select value={form.pageType} onChange={e => update('pageType', e.target.value)}>
                  <option value="service">Service page</option>
                  <option value="location-service">Location + service page</option>
                  <option value="homepage">Homepage</option>
                  <option value="blog">Blog / article</option>
                  <option value="contact">Contact page</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>USP or offer (optional)</label>
              <input
                type="text"
                placeholder="e.g. From £25, free quotes, 5-star rated, since 2008"
                value={form.usp}
                onChange={e => update('usp', e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Phone number (optional)</label>
              <input
                type="text"
                placeholder="e.g. 01737 773 768"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
              />
            </div>
          </div>

          {error && <div className="alert alert-error" style={{ marginTop: 0 }}>{error}</div>}

          <button
            className="btn btn-primary"
            onClick={generate}
            disabled={generating || !form.keyword.trim() || !form.businessName.trim()}
            style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
          >
            {generating ? <><span className="spinner" /> Generating…</> : 'Generate meta data'}
          </button>
        </div>

        {/* Right — results */}
        <div>
          {!results && !generating && (
            <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem', color: 'var(--gray-400)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🏷️</div>
              <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>
                Fill in the details and click generate to create your meta data variations
              </div>
            </div>
          )}

          {generating && (
            <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <span className="spinner" style={{ width: 24, height: 24 }} />
              <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 12 }}>Generating 3 variations of each…</div>
            </div>
          )}

          {results && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>Results</div>
                <button className="btn btn-sm" onClick={copyAll}>
                  {copied === 'all' ? '✓ Copied!' : 'Copy all'}
                </button>
              </div>

              <div className="section-label" style={{ marginTop: 0 }}>Title tags (max 60 chars)</div>
              {results.titleTags.map((title, i) => (
                <MetaResult
                  key={`title-${i}`}
                  label={`Option ${i + 1}`}
                  value={title}
                  limit={60}
                  onRegenerate={() => regenerateOne('title', i)}
                  regenerating={regenIndex === `title-${i}`}
                  onCopy={() => handleCopy(`title-${i}`)}
                />
              ))}

              <div className="section-label">Meta descriptions (max 156 chars)</div>
              {results.metaDescriptions.map((desc, i) => (
                <MetaResult
                  key={`desc-${i}`}
                  label={`Option ${i + 1}`}
                  value={desc}
                  limit={156}
                  onRegenerate={() => regenerateOne('desc', i)}
                  regenerating={regenIndex === `desc-${i}`}
                  onCopy={() => handleCopy(`desc-${i}`)}
                />
              ))}

              <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginTop: 4 }}>
                <button className="btn btn-sm" onClick={generate} disabled={generating}>
                  ↻ Regenerate all
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
